import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarCheck,
  BookOpen,
  Dumbbell,
  MessageSquare,
  Truck,
  Bell,
  History,
  QrCode,
  Megaphone,
  Gavel,
  Calendar,
  Newspaper,
  Users,
  UserCheck,
  Network,
  FileText,
  FileCheck,
  FolderGit2,
  MapPin,
  Image,
  Printer,
  Settings,
  CreditCard,
  Banknote,
  LogOut,
  X,
  Search,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { logout } from '../../services/authService';
import './Sidebar.scss';

const Sidebar = ({ isOpen = true, onClose, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const user = useMemo(() => {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, []);

  const role = (user?.userType || user?.role || 'admin').toLowerCase();
  const roleLabel =
    role === 'muhtar'
      ? 'Mahalle Muhtarı'
      : role === 'library'
        ? 'Kütüphane Personeli'
        : role === 'printer'
          ? 'Toner Sorumlusu'
          : role === 'sports' || role === 'sporsuperadmin'
            ? 'Spor Sorumlusu'
            : role === 'regionalsports'
              ? 'Bölge Sorumlusu'
              : role === 'istirakler'
                ? 'İştirak Yetkilisi'
                : role === 'basin'
                  ? 'Basın Yayın Merkezi'
                  : role === 'department'
                    ? 'Birim Personeli'
                    : 'Yönetici';
  const displayName = user?.nameSurname || user?.name || 'Kullanıcı';

  const [qrModeEnabled, setQrModeEnabled] = useState(() => {
    const stored = localStorage.getItem('libraryQrModeEnabled');
    return stored !== 'false';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('libraryQrModeEnabled');
      setQrModeEnabled(stored !== 'false');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('libraryQrModeChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('libraryQrModeChanged', handleStorageChange);
    };
  }, []);

  // Menu groups designed in Arc UI categorized style
  const menuGroups = useMemo(() => {
    if (role === 'muhtar') {
      return [
        {
          id: 'hizmet',
          title: 'Hizmet & Mahalle',
          items: [{ to: '/neighborhood-developments', text: 'Mahallem Gelişiyor', icon: MapPin }]
        }
      ];
    }

    if (role === 'library') {
      return [
        {
          id: 'library',
          title: 'Kütüphane Hizmetleri',
          items: [
            { to: '/library-appointments', text: 'Kütüphane Randevuları', icon: CalendarCheck },
            { to: '/books', text: 'Kitap Yönetimi', icon: BookOpen },
            ...(qrModeEnabled ? [{ to: '/library-appointments/scan', text: 'QR Tara', icon: QrCode }] : [])
          ]
        }
      ];
    }

    if (role === 'printer') {
      return [
        {
          id: 'printer',
          title: 'Donanım & Sarf',
          items: [{ to: '/printers', text: 'Toner Yönetimi', icon: Printer }]
        }
      ];
    }

    if (role === 'sports' || role === 'sporsuperadmin') {
      return [
        {
          id: 'sports',
          title: 'Spor Tesisleri',
          items: [
            { to: '/sports-appointments', text: 'Spor Randevuları', icon: Dumbbell },
            { to: '/sports/users', text: 'Kullanıcı Yönetimi', icon: Users },
            { to: '/sports/activity-logs', text: 'Aktivite Kayıtları', icon: History }
          ]
        }
      ];
    }

    if (role === 'regionalsports') {
      return [
        {
          id: 'sports',
          title: 'Bölge Spor',
          items: [{ to: '/sports-appointments', text: 'Spor Randevuları', icon: Dumbbell }]
        }
      ];
    }

    if (role === 'istirakler') {
      return [
        {
          id: 'istirak',
          title: 'İştirak Operasyonları',
          items: [
            { to: '/sports-appointments', text: 'Spor Randevuları', icon: Dumbbell },
            { to: '/sports/activity-logs', text: 'Aktivite Kayıtları', icon: History },
            { to: '/club-applications', text: 'Kart Başvuruları', icon: CreditCard },
            { to: '/club-payments', text: 'Kulüp Ödemeleri', icon: Banknote }
          ]
        }
      ];
    }

    if (role === 'basin') {
      return [
        {
          id: 'basin',
          title: 'Basın & Medya',
          items: [
            { to: '/announcements', text: 'Duyurular', icon: Megaphone },
            { to: '/events', text: 'Etkinlikler', icon: Calendar },
            { to: '/news', text: 'Haberler', icon: Newspaper },
            { to: '/press-coverage', text: 'Basında Biz', icon: Newspaper },
            { to: '/projects', text: 'Proje Yönetimi', icon: FolderGit2 },
            { to: '/neighborhood-developments', text: 'Mahallem Gelişiyor', icon: MapPin }
          ]
        }
      ];
    }

    if (role === 'department') {
      return [
        {
          id: 'dept',
          title: 'Birim İşlemleri',
          items: [
            { to: '/report-incidents', text: 'Bize Bildir Yönetimi', icon: MessageSquare },
            { to: '/neighborhood-developments', text: 'Mahallem Gelişiyor', icon: MapPin }
          ]
        }
      ];
    }

    // Default: Admin groups
    return [
      {
        id: 'overview',
        title: 'Genel & Özet',
        items: [
          { to: '/', text: 'Ana Sayfa', icon: LayoutDashboard },
          { to: '/sports/activity-logs', text: 'Aktivite Kayıtları', icon: History },
          ...(process.env.NODE_ENV === 'development'
            ? [{ to: '/mobile-notifications', text: 'Mobil Bildirimler', icon: Bell }]
            : [])
        ]
      },
      {
        id: 'services',
        title: 'Vatandaş & Hizmet',
        items: [
          { to: '/report-incidents', text: 'Bize Bildir Yönetimi', icon: MessageSquare },
          { to: '/sitas', text: 'SİTAS Operasyon', icon: Truck },
          { to: '/neighborhood-developments', text: 'Mahallem Gelişiyor', icon: MapPin },
          { to: '/parlament-decisions', text: 'Meclis Kararları', icon: FileCheck },
          { to: '/tenders', text: 'İhaleler', icon: Gavel }
        ]
      },
      {
        id: 'culture_sports',
        title: 'Kültür, Spor & Randevu',
        items: [
          { to: '/library-appointments', text: 'Kütüphane Randevuları', icon: CalendarCheck },
          { to: '/books', text: 'Kitap Yönetimi', icon: BookOpen },
          ...(qrModeEnabled ? [{ to: '/library-appointments/scan', text: 'QR Tara', icon: QrCode }] : []),
          { to: '/sports-appointments', text: 'Spor Randevuları', icon: Dumbbell }
        ]
      },
      {
        id: 'content',
        title: 'İçerik & Yayın',
        items: [
          { to: '/announcements', text: 'Duyurular', icon: Megaphone },
          { to: '/events', text: 'Etkinlikler', icon: Calendar },
          { to: '/news', text: 'Haberler', icon: Newspaper },
            { to: '/press-coverage', text: 'Basında Biz', icon: Newspaper },
          { to: '/projects', text: 'Proje Yönetimi', icon: FolderGit2 },
          { to: '/advertisements', text: 'İlanlar', icon: Megaphone },
          { to: '/sliders', text: 'Slider Yönetimi', icon: Image },
          { to: '/parlament-members', text: 'Meclis Üyeleri', icon: Users },
          { to: '/managers', text: 'Müdürlerimiz', icon: UserCheck },
          ...(process.env.NODE_ENV === 'development'
            ? [{ to: '/organization', text: 'Organizasyon Şeması', icon: Network }]
            : []),
          { to: '/activity-reports', text: 'Faaliyet Raporları', icon: FileText }
        ]
      },
      {
        id: 'system',
        title: 'Sistem & Yönetim',
        items: [
          { to: '/printers', text: 'Toner Yönetimi', icon: Printer },
          { to: '/settings', text: 'Ayarlar', icon: Settings }
        ]
      }
    ];
  }, [role, qrModeEnabled]);

  const filteredGroups = useMemo(() => {
    if (!searchFilter.trim()) return menuGroups;
    const filter = searchFilter.toLowerCase();

    return menuGroups
      .map((group) => {
        const matchingItems = group.items.filter((item) =>
          item.text.toLowerCase().includes(filter)
        );
        return {
          ...group,
          items: matchingItems
        };
      })
      .filter((group) => group.items.length > 0);
  }, [menuGroups, searchFilter]);

  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleLinkClick = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      onClose?.();
    }
  };

  return (
    <>
      <aside className={`arc-sidebar ${isOpen ? 'is-open' : 'is-closed'}`}>
        {/* Brand Header */}
        <div className="arc-sidebar-header">
          <Link to="/" className="arc-sidebar-brand" onClick={handleLinkClick}>
            <div className="arc-brand-logo-wrap">
              <img src="/logo.png" alt="İskenderun Belediyesi" className="arc-brand-img" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <div className="arc-brand-text">
              <span className="arc-brand-title">İSKENDERUN</span>
              <span className="arc-brand-subtitle">Belediyesi</span>
            </div>
          </Link>
          <button
            type="button"
            className="arc-sidebar-close"
            onClick={onClose}
            aria-label="Menüyü kapat"
          >
            <X size={18} />
          </button>
        </div>

        {/* Sidebar Search Filter */}
        <div className="arc-sidebar-search-wrap">
          <div className="arc-sidebar-search">
            <Search size={14} className="arc-sidebar-search-icon" />
            <input
              type="text"
              placeholder="Menüde filtrele..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="arc-sidebar-search-input"
            />
            {searchFilter && (
              <button
                type="button"
                className="arc-sidebar-search-clear"
                onClick={() => setSearchFilter('')}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Navigation Groups */}
        <div className="arc-sidebar-content">
          {filteredGroups.map((group) => {
            const isCollapsed = collapsedGroups[group.id];
            return (
              <div key={group.id} className="arc-nav-group">
                <button
                  type="button"
                  className="arc-nav-group-header"
                  onClick={() => toggleGroupCollapse(group.id)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="arc-group-title">{group.title}</span>
                  <div className="arc-group-meta">
                    <span className="arc-group-count">{group.items.length}</span>
                    {isCollapsed ? (
                      <ChevronRight size={13} className="arc-group-chevron" />
                    ) : (
                      <ChevronDown size={13} className="arc-group-chevron" />
                    )}
                  </div>
                </button>

                {!isCollapsed && (
                  <nav className="arc-nav-group-items">
                    {group.items.map((item, idx) => {
                      const Icon = item.icon;
                      const active = isActive(item.to);
                      return (
                        <Link
                          key={idx}
                          to={item.to}
                          className={`arc-nav-link ${active ? 'active' : ''}`}
                          onClick={handleLinkClick}
                        >
                          <span className="arc-nav-pill-indicator" />
                          <Icon size={17} className="arc-nav-icon" />
                          <span className="arc-nav-text">{item.text}</span>
                        </Link>
                      );
                    })}
                  </nav>
                )}
              </div>
            );
          })}
        </div>

        {/* Sidebar Footer */}
        <div className="arc-sidebar-footer">
          <div className="arc-footer-user">
            <div className="arc-footer-avatar">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div className="arc-footer-info">
              <span className="arc-footer-name">{displayName}</span>
              <span className="arc-footer-role">{roleLabel}</span>
            </div>
          </div>
          <button
            type="button"
            className="arc-footer-logout"
            onClick={handleLogout}
            title="Güvenli Çıkış Yap"
          >
            <LogOut size={16} />
            <span>Çıkış</span>
          </button>
        </div>
      </aside>

      {/* Backdrop for Mobile overlay */}
      {isOpen && (
        <div
          className="arc-sidebar-backdrop show"
          onClick={onClose}
          aria-hidden="false"
        />
      )}
    </>
  );
};

export default Sidebar;
